import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';
import { getProductCodeFromProductId, isBrowser } from '@noths/polaris-client-utils';

import { selectUserAppliedFilterTitles } from 'src/components/organisms/Filter/modules/selectors/selectors';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectCurrentPage, selectTotalProducts } from 'src/redux/selectors';
import { actions as visibilityTrackingActions } from 'src/redux/visibilityTracking';
import { ClearEcommerceObject } from 'src/types/googleAnalytics';
import { getImpressionFieldObject } from 'src/utils/googleAnalytics';
import { PRODUCT_IMPRESSION_BATCH_SIZE, PRODUCT_IMPRESSION_TIMEOUT_MS } from '../constants';
import { selectProductFromCode } from '../selectors';
import { requestProducts, updateFilters, UpdateFiltersActionType } from '../thunks';
import {
  FilterUpdateImpressionsEvent,
  LoadImpressionsActionType,
  LoadProductImpressionsEvent,
  ProductVisibleImpressionsEvent,
} from './events';

type RequestProductsAction = ReturnType<(typeof requestProducts)['fulfilled']>;
type UpdateFiltersAction = ReturnType<(typeof updateFilters)['fulfilled']>;

const isLoadPreviousProductsRequest = (nextState: ReduxApplicationState, currentPage: number) => {
  return selectCurrentPage(nextState) > currentPage;
};

const isLoadMoreProductsRequest = (
  prevState: ReduxApplicationState,
  nextState: ReduxApplicationState,
  currentPage: number,
) => {
  return (
    selectCurrentPage(prevState) < selectCurrentPage(nextState) &&
    selectCurrentPage(nextState) === currentPage
  );
};

const sendProductImpressionsEvent = (
  productImpressionQueue: number[],
  state: ReduxApplicationState,
) => {
  sendGAEvent<ClearEcommerceObject>({
    ecommerce: null,
  });

  sendGAEvent<ProductVisibleImpressionsEvent>({
    event: 'product_impressions',
    event_category: 'Product listings',
    event_action: 'Product Impressions',
    event_label: 'Impressions',
    ecommerce: {
      impressions: productImpressionQueue.map((productId) => {
        const product = selectProductFromCode(state, getProductCodeFromProductId(productId));

        return getImpressionFieldObject(product!, state);
      }),
    },
  });
};

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [visibilityTrackingActions.sendProductImpressionBatch.type]: (prevState, _, __, dispatch) => {
    const { productImpressionQueue } = prevState.visibilityTracking;

    if (!isBrowser() || productImpressionQueue.length === 0) {
      return;
    }

    sendProductImpressionsEvent(productImpressionQueue, prevState);

    // Clear the impression queue in the store
    dispatch(visibilityTrackingActions.clearProductImpressionQueue());
  },
  [requestProducts.pending.type]: (_prevState, _nextState, _action, dispatch) => {
    dispatch(visibilityTrackingActions.sendProductImpressionBatch());
  },
  [visibilityTrackingActions.onProductVisible.type]: (prevState, nextState, _action, dispatch) => {
    const { productImpressionQueue } = nextState.visibilityTracking;

    if (productImpressionQueue.length === PRODUCT_IMPRESSION_BATCH_SIZE) {
      // We have reached the batch limit and should send the batch
      dispatch(visibilityTrackingActions.sendProductImpressionBatch());
      return;
    }

    if (!prevState.visibilityTracking.atLeastOneProductHasBecomeVisible) {
      // Send batch on an interval so we can ensure event is sent during inactivity
      setInterval(
        () => dispatch(visibilityTrackingActions.sendProductImpressionBatch()),
        PRODUCT_IMPRESSION_TIMEOUT_MS,
      );

      // Send batch if user navigates away from page
      window.onbeforeunload = () => {
        dispatch(visibilityTrackingActions.sendProductImpressionBatch());
        return; // Empty return needed to prevent dialog box showing before unload
      };
    }
  },
  [requestProducts.fulfilled.type]: (prevState, nextState, action) => {
    const { payload } = action as RequestProductsAction;
    const isLoadPreviousRequest = isLoadPreviousProductsRequest(nextState, payload.currentPage);
    const isLoadMoreRequest = isLoadMoreProductsRequest(prevState, nextState, payload.currentPage);

    if (isLoadPreviousRequest || isLoadMoreRequest) {
      const eventAction: LoadImpressionsActionType = isLoadPreviousRequest
        ? 'Load Previous Impressions'
        : 'Load More Impressions';

      sendGAEvent<LoadProductImpressionsEvent>({
        event: 'product_impressions',
        event_action: eventAction,
        event_category: 'Product listings',
        event_label: String(selectCurrentPage(prevState)),
      });
    }
  },
  [updateFilters.fulfilled.type]: (state, _, action) => {
    const { payload } = action as UpdateFiltersAction;

    if (payload === UpdateFiltersActionType.FILTER) {
      const updatedTotalProducts = selectTotalProducts(state);
      const appliedFilterTitles = selectUserAppliedFilterTitles(state);

      sendGAEvent<FilterUpdateImpressionsEvent>({
        event: 'custom_event',
        event_category: 'Filters',
        event_action: 'applied filter info',
        event_label: appliedFilterTitles,
        total_listing_items: updatedTotalProducts,
      });
    }
  },
};
